import React, { Component } from "react";
import * as Http from "../../http";
import * as Business from "../../utils/business";
import Loading from "../../components/Loading";

const MODULE_CONFIG = [
  {
    key: "car",
    label: "CAR",
    icon: "pe-7s-note2",
    color: "#0B1437",
    accent: "#FFE600",
  },
  {
    key: "icaap",
    label: "ICAAP",
    icon: "pe-7s-graph1",
    color: "#1D4ED8",
    accent: "#BAE6FD",
  },
  {
    key: "raroc",
    label: "RAROC",
    icon: "pe-7s-calculator",
    color: "#065F46",
    accent: "#6EE7B7",
  },
  {
    key: "pricing",
    label: "PRICING",
    icon: "pe-7s-cash",
    color: "#7C3AED",
    accent: "#DDD6FE",
  },
];

const STATUS_LABEL = {
  PENDING: { label: "Pending", bg: "#FEF3C7", color: "#92400E" },
  CALCULATED: { label: "Calculated", bg: "#D1FAE5", color: "#065F46" },
  APPROVED: { label: "Approved", bg: "#DBEAFE", color: "#1E40AF" },
  REJECTED: { label: "Rejected", bg: "#FEE2E2", color: "#991B1B" },
};

function StatCard({ icon, label, value, color, accent, subtitle }) {
  return (
    <div className="ss-card" style={{ borderTop: `4px solid ${color}` }}>
      <div
        className="ss-card__icon"
        style={{ background: color, color: accent }}
      >
        <i className={icon} />
      </div>
      <div className="ss-card__body">
        <div className="ss-card__value">{value}</div>
        <div className="ss-card__label">{label}</div>
        {subtitle && <div className="ss-card__sub">{subtitle}</div>}
      </div>
    </div>
  );
}

function StatusPills({ byStatus }) {
  if (!byStatus) return null;
  const entries = Object.entries(byStatus);
  if (entries.length === 0) return null;
  return (
    <div className="ss-status-pills">
      {entries.map(([status, count]) => {
        const cfg = STATUS_LABEL[status] || {
          label: status,
          bg: "#F3F4F6",
          color: "#374151",
        };
        return (
          <span
            key={status}
            className="ss-status-pill"
            style={{ background: cfg.bg, color: cfg.color }}
          >
            {cfg.label}: {count}
          </span>
        );
      })}
    </div>
  );
}

export default class SystemStats extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, data: null, error: null };
  }

  componentDidMount() {
    Http.getDashboardStats()
      .then((res) => {
        if (res && res.Success) {
          this.setState({ loading: false, data: res.Data });
        } else {
          this.setState({
            loading: false,
            error: "Failed to load statistics.",
          });
        }
      })
      .catch(() => this.setState({ loading: false, error: "Network error." }));
  }

  renderModuleCards(data) {
    return (
      <div className="ss-module-grid">
        {MODULE_CONFIG.map((mod) => {
          const info = data[mod.key] || {};
          return (
            <div
              key={mod.key}
              className="ss-module-tile"
              style={{ borderLeft: `5px solid ${mod.color}` }}
            >
              <div
                className="ss-module-tile__header"
                style={{ background: mod.color }}
              >
                <i
                  className={mod.icon}
                  style={{ color: mod.accent, fontSize: 22, marginRight: 8 }}
                />
                <span
                  style={{
                    color: mod.accent,
                    fontWeight: 700,
                    fontSize: 15,
                    letterSpacing: 2,
                  }}
                >
                  {mod.label}
                </span>
              </div>
              <div className="ss-module-tile__body">
                <div className="ss-module-tile__total">
                  <span className="ss-big-num">{info.total || 0}</span>
                  <span className="ss-big-label">Total Files</span>
                </div>
                <StatusPills byStatus={info.byStatus} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderTopRow(data) {
    return (
      <div className="ss-top-row">
        <StatCard
          icon="pe-7s-users"
          label="System Users"
          value={data.users ? data.users.total : 0}
          color="#0B1437"
          accent="#FFE600"
        />
        <StatCard
          icon="pe-7s-folder"
          label="CAR Files"
          value={(data.car || {}).total || 0}
          color="#0B1437"
          accent="#FFE600"
          subtitle="Upload Management"
        />
        <StatCard
          icon="pe-7s-graph1"
          label="ICAAP Files"
          value={(data.icaap || {}).total || 0}
          color="#1D4ED8"
          accent="#BAE6FD"
          subtitle="Upload Management"
        />
        <StatCard
          icon="pe-7s-calculator"
          label="RAROC Files"
          value={(data.raroc || {}).total || 0}
          color="#065F46"
          accent="#6EE7B7"
          subtitle="RAROC Records"
        />
        <StatCard
          icon="pe-7s-cash"
          label="Pricing Records"
          value={(data.pricing || {}).total || 0}
          color="#7C3AED"
          accent="#DDD6FE"
          subtitle="Pricing Info"
        />
      </div>
    );
  }

  renderRecentActivity(data) {
    const logs = data.recentActivity || [];
    if (logs.length === 0) return null;
    return (
      <div className="ss-activity">
        <div className="ss-section-title">
          <i className="pe-7s-clock" /> Recent Activity
        </div>
        <table className="ey-file-table ss-activity-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Action</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, idx) => (
              <tr key={log.action_log_id || idx}>
                <td>{idx + 1}</td>
                <td className="ey-file-name">{log.username || "—"}</td>
                <td>{log.action || "—"}</td>
                <td>{log.time || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderBarChart(data) {
    const modules = MODULE_CONFIG.map((m) => ({
      label: m.label,
      value: (data[m.key] || {}).total || 0,
      color: m.color,
      accent: m.accent,
    }));
    const maxVal = Math.max(...modules.map((m) => m.value), 1);
    return (
      <div className="ss-chart">
        <div className="ss-section-title">
          <i className="pe-7s-graph2" /> File Count by Module
        </div>
        <div className="ss-bar-chart">
          {modules.map((m) => (
            <div key={m.label} className="ss-bar-item">
              <div className="ss-bar-track">
                <div
                  className="ss-bar-fill"
                  style={{
                    height: `${Math.round((m.value / maxVal) * 120)}px`,
                    background: m.color,
                  }}
                />
              </div>
              <div className="ss-bar-val">{m.value}</div>
              <div className="ss-bar-lbl" style={{ color: m.color }}>
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div style={{ height: Business.getHeightContent() }}>
          <Loading size="small" />
        </div>
      );
    }
    if (this.state.error || !this.state.data) {
      return (
        <div className="ss-error">
          <i className="pe-7s-info" />{" "}
          {this.state.error || "No data available."}
        </div>
      );
    }
    const { data } = this.state;
    return (
      <div className="ss-wrapper">
        <div className="ss-header">
          <i className="pe-7s-display2 ss-header__icon" />
          <div>
            <div className="ss-header__title">System Dashboard</div>
            <div className="ss-header__sub">
              Overview of all modules and system activity
            </div>
          </div>
        </div>
        {this.renderTopRow(data)}
        <div className="ss-mid-row">
          {this.renderBarChart(data)}
          {this.renderRecentActivity(data)}
        </div>
        {this.renderModuleCards(data)}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// src/pages/Dashboard/SystemStats.js